'use client';
import React, { useState } from 'react';
import { Play, Pause, Square, Layers, Save, GitCompare, Activity, AlertTriangle, Trash2, PanelLeftOpen, PanelLeftClose, MoreHorizontal } from 'lucide-react';
import type { SimulationState } from './ArchitectureCanvasScreen';

interface CanvasToolbarProps {
  simState: SimulationState;
  setSimState: (s: SimulationState) => void;
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  onOpenPresets: () => void;
  onOpenSaveLoad: () => void;
  onOpenCompare: () => void;
  onOpenBottleneck: () => void;
  onOpenPathMonitor: () => void;
  onClear: () => void;
  nodeCount: number;
  edgeCount: number;
  isLandscape?: boolean;
}

export default function CanvasToolbar({
  simState, setSimState,
  paletteOpen, setPaletteOpen,
  onOpenPresets, onOpenSaveLoad, onOpenCompare,
  onOpenBottleneck, onOpenPathMonitor, onClear,
  nodeCount, edgeCount,
  isLandscape = false,
}: CanvasToolbarProps) {

  const [mobileExpanded, setMobileExpanded] = useState(false);
  const simColor = simState === 'running' ? 'text-positive' : simState === 'paused' ? 'text-warning' : 'text-muted-foreground';

  // Landscape game-style toolbar: ultra-compact single row
  if (isLandscape) {
    return (
      <div className="border-b border-border/60 bg-card/95 backdrop-blur-sm shrink-0 game-border" style={{ height: 36 }}>
        <div className="h-full flex items-center gap-0.5 px-1.5">
          {/* Palette toggle */}
          <button
            onClick={() => setPaletteOpen(!paletteOpen)}
            className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
            title={paletteOpen ? 'Hide palette' : 'Show palette'}
          >
            {paletteOpen ? <PanelLeftClose size={13} /> : <PanelLeftOpen size={13} />}
          </button>

          <div className="w-px h-4 bg-border/60 mx-0.5" />

          {/* Icon-only action buttons */}
          <button onClick={onOpenPresets} className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all" title="Presets">
            <Layers size={12} />
          </button>
          <button onClick={onOpenSaveLoad} className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all" title="Save/Load">
            <Save size={12} />
          </button>
          <button onClick={onOpenBottleneck} className="p-1 rounded text-muted-foreground hover:text-warning hover:bg-warning/10 transition-all" title="Bottleneck">
            <AlertTriangle size={12} />
          </button>
          <button onClick={onOpenPathMonitor} className="p-1 rounded text-muted-foreground hover:text-info hover:bg-info/10 transition-all" title="Path Monitor">
            <Activity size={12} />
          </button>

          {/* Node/edge count */}
          <div className="flex items-center gap-1 px-1 text-[9px] font-mono text-muted-foreground/60">
            <span className="w-1 h-1 rounded-full bg-info inline-block" />{nodeCount}
            <span className="w-1 h-1 rounded-full bg-muted-foreground/40 inline-block ml-0.5" />{edgeCount}
          </div>

          <div className="flex-1" />

          {/* Simulation controls — compact */}
          <div className="flex items-center gap-0.5">
            {simState === 'stopped' && (
              <button
                onClick={() => setSimState('running')}
                disabled={nodeCount === 0}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-positive/15 text-positive hover:bg-positive/25 border border-positive/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-game-glow"
              >
                <Play size={10} />
                RUN
              </button>
            )}
            {simState === 'running' && (
              <>
                <button onClick={() => setSimState('paused')} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-warning/15 text-warning hover:bg-warning/25 border border-warning/40 transition-all">
                  <Pause size={10} />
                </button>
                <button onClick={() => setSimState('stopped')} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-danger/15 text-danger hover:bg-danger/25 border border-danger/40 transition-all">
                  <Square size={10} />
                </button>
              </>
            )}
            {simState === 'paused' && (
              <>
                <button onClick={() => setSimState('running')} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-positive/15 text-positive hover:bg-positive/25 border border-positive/40 transition-all">
                  <Play size={10} />
                </button>
                <button onClick={() => setSimState('stopped')} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-secondary text-muted-foreground hover:text-foreground transition-all">
                  <Square size={10} />
                </button>
              </>
            )}

            {simState === 'running' && (
              <span className="relative flex h-1.5 w-1.5 ml-1">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-positive opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-positive" />
              </span>
            )}
          </div>

          <div className="w-px h-4 bg-border/60 mx-0.5" />

          {/* Clear */}
          <button
            onClick={onClear}
            disabled={nodeCount === 0}
            className="p-1 rounded text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            title="Clear canvas"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-border bg-card/90 backdrop-blur-sm shrink-0">
      {/* Primary row */}
      <div className="h-12 sm:h-11 flex items-center gap-1 px-2 sm:px-3">
        {/* Palette toggle */}
        <button
          onClick={() => setPaletteOpen(!paletteOpen)}
          className="w-10 h-10 sm:w-auto sm:h-auto p-1.5 flex items-center justify-center rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
          title={paletteOpen ? 'Hide palette' : 'Show palette'}
        >
          {paletteOpen ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
        </button>

        <div className="w-px h-5 bg-border mx-1 hidden sm:block" />

        {/* Canvas stats - hidden on very small screens */}
        <div className="hidden sm:flex items-center gap-2 text-xs font-mono-nums text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-info inline-block" />
            {nodeCount}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
            {edgeCount}
          </span>
        </div>

        <div className="w-px h-5 bg-border mx-1 hidden md:block" />

        {/* Desktop action buttons */}
        <div className="hidden md:flex items-center gap-0.5">
          <button onClick={onOpenPresets} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            <Layers size={13} />
            Presets
          </button>
          <button onClick={onOpenSaveLoad} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            <Save size={13} />
            Save/Load
          </button>
          <button onClick={onOpenCompare} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            <GitCompare size={13} />
            Compare
          </button>
          <div className="w-px h-5 bg-border mx-1" />
          <button onClick={onOpenBottleneck} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            <AlertTriangle size={13} />
            Bottleneck
          </button>
          <button onClick={onOpenPathMonitor} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            <Activity size={13} />
            Path Monitor
          </button>
        </div>

        {/* Mobile icon-only action buttons */}
        <div className="flex md:hidden items-center gap-0.5">
          <button onClick={onOpenPresets} className="w-10 h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all" title="Presets">
            <Layers size={14} />
          </button>
          <button onClick={onOpenSaveLoad} className="w-10 h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all" title="Save/Load">
            <Save size={14} />
          </button>
          <div className="relative">
            <button
              onClick={() => setMobileExpanded(!mobileExpanded)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
              title="More canvas actions"
              aria-label="More canvas actions"
            >
              <MoreHorizontal size={16} />
            </button>
            {mobileExpanded && (
              <div className="absolute left-0 top-10 z-50 w-40 overflow-hidden rounded-lg border border-border bg-card shadow-xl">
                <button onClick={() => { onOpenBottleneck(); setMobileExpanded(false); }} className="flex w-full items-center gap-2.5 px-3 py-3 text-left text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground">
                  <AlertTriangle size={14} />
                  Bottleneck
                </button>
                <button onClick={() => { onOpenPathMonitor(); setMobileExpanded(false); }} className="flex w-full items-center gap-2.5 px-3 py-3 text-left text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground">
                  <Activity size={14} />
                  Path Monitor
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1" />

        {/* Simulation controls */}
        <div className="flex items-center gap-1">
          {simState === 'stopped' && (
            <button
              onClick={() => setSimState('running')}
              disabled={nodeCount === 0}
              className="flex h-10 items-center gap-1 sm:gap-1.5 px-2 sm:h-auto sm:px-3 sm:py-1 rounded-md text-xs font-semibold bg-positive/10 text-positive hover:bg-positive/20 border border-positive/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Play size={12} />
              <span className="hidden sm:inline">Run Simulation</span>
              <span className="sm:hidden">Run</span>
            </button>
          )}
          {simState === 'running' && (
            <>
              <button onClick={() => setSimState('paused')} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-md text-xs font-medium bg-warning/10 text-warning hover:bg-warning/20 border border-warning/30 transition-all">
                <Pause size={12} />
                <span className="hidden sm:inline">Pause</span>
              </button>
              <button onClick={() => setSimState('stopped')} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-md text-xs font-medium bg-danger/10 text-danger hover:bg-danger/20 border border-danger/30 transition-all">
                <Square size={12} />
                <span className="hidden sm:inline">Stop</span>
              </button>
            </>
          )}
          {simState === 'paused' && (
            <>
              <button onClick={() => setSimState('running')} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-md text-xs font-semibold bg-positive/10 text-positive hover:bg-positive/20 border border-positive/30 transition-all">
                <Play size={12} />
                <span className="hidden sm:inline">Resume</span>
              </button>
              <button onClick={() => setSimState('stopped')} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-md text-xs font-medium bg-secondary text-muted-foreground hover:text-foreground transition-all">
                <Square size={12} />
                <span className="hidden sm:inline">Stop</span>
              </button>
            </>
          )}

          <div className={`hidden sm:flex items-center gap-1.5 ml-1 ${simColor}`}>
            {simState === 'running' && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-positive opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-positive" />
              </span>
            )}
            <span className="text-xs font-medium capitalize font-mono-nums">{simState}</span>
          </div>
        </div>

        <div className="w-px h-5 bg-border mx-1" />

        {/* Clear */}
        <button
          onClick={onClear}
          disabled={nodeCount === 0}
          className="w-10 h-10 flex items-center justify-center sm:w-auto sm:h-auto sm:p-1.5 rounded-md text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          title="Clear canvas"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}